import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { GridModule } from '@progress/kendo-angular-grid';
import { DashboardService } from '../../dashboard/dashboard.service';
import { MatButtonModule } from '@angular/material/button';
import { AdminService } from '../../admin/admin.service';
import { AsyncPipe, CommonModule, DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxLoadingModule } from 'ngx-loading';
import { Subject, takeUntil, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { BookManagementService } from '../book-management.service';
import { SessionStorageService } from '../../services/session-storage.service';

@Component({
  selector: 'app-policy-drilldown',
  imports: [
    NgxLoadingModule,
    MatSelectModule,
    GridModule,
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    AsyncPipe,
  ],
  templateUrl: './policy-drilldown.component.html',
  styleUrl: './policy-drilldown.component.scss',
  providers: [DecimalPipe],
})
export class PolicyDrilldownComponent implements OnInit, OnDestroy {
  private dbs = inject(DashboardService);
  private cdRef = inject(ChangeDetectorRef);
  private admnSrvc = inject(AdminService);
  private bmSvc = inject(BookManagementService);
  private route = inject(Router);
  private ss = inject(SessionStorageService);
  private decimalPipe = inject(DecimalPipe);
  @Input() view = 'ho';
  @Output() emitDrilldownFilter = new EventEmitter();
  unsubscribe: Subject<any> = new Subject();
  private widgetKey = this.bmSvc.nextWidgetKey('contract-summary');
  loading$ = new BehaviorSubject<boolean>(false);
  get loading(): boolean {
    return this.loading$.value;
  }
  set loading(value: boolean) {
    if (this.loading$.value === value) {
      return;
    }
    this.loading$.next(value);
    this.bmSvc.setWidgetLoading(this.widgetKey, value);
  }

  emittingFilters = false;
  headerType: any = 'rep';
  selectedGroup: any = 'pm-dashboard';
  filterValues: any;
  reportName: any = 'contract-summary-carrier-drilldown';
  groupByItems: any;
  skip = 0;
  take = 10;
  pageSizeOptions = [10, 25, 50, 75, 100];
  total = 0;
  gridData: { headers: any[]; data: any[]; total: number } = {
    headers: [],
    data: [],
    total: 0,
  };
  originalHeaders: any[] = [];
  private readonly drilldownHierarchy = [
    'carrier',
    'productType',
    'productName',
    'producer',
    'policy',
  ];
  // Maps a camelCase hierarchy level to the PascalCase `group` the report expects.
  private readonly groupByLevel: Record<string, string> = {
    carrier: 'Carrier',
    productType: 'ProductType',
    productName: 'ProductName',
    producer: 'Producer',
  };
  private readonly groupLabels: Record<string, string> = {
    carrier: 'Carrier',
    productType: 'Product Type',
    productName: 'Product Name',
    producer: 'Producer',
  };
  private readonly fieldLabels: Record<string, string> = {
    carrier: 'Carrier',
    productType: 'Product Type',
    productName: 'Product Name',
  };
  private readonly scopeKeyByLevel: Record<string, string> = {
    carrier: 'carrier',
    productType: 'productType',
    productName: 'productName',
  };
  private currentGroup = 'carrier';
  activeSelections: Record<string, string[]> = {};
  currentLevelLabel = 'Carrier';
  drilldownCrumbs: { level: string; levelLabel: string; label: string }[] = [];
  canLaunchToRepView = false;
  private getViewMode(view: string) {
    switch (view) {
      case 'ho':
        return 'Unit';
      case 'rep':
        return 'Rep';
      case 'carrier':
        return 'Carrier';
      default:
        return '';
    }
  }

  ngOnInit() {
    this.bmSvc.updateBmWidgets$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(filterVals => {
        if (this.emittingFilters) {
          this.emittingFilters = false;
          return;
        }
        this.resetDrilldown();
        this.filterValues = filterVals;
        this.getUiConfig();
      });

    if (this.view !== 'carrier') {
      this.canLaunchToRepView = true;
    }
  }

  ngOnDestroy() {
    this.bmSvc.setWidgetLoading(this.widgetKey, false);
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUiConfig() {
    this.gridData.headers = [];
    this.loading = true;
    const viewMode = this.getViewMode(this.view).toLowerCase();

    this.admnSrvc
      .getUIConfigHeaders({
        uiconfig: 'pm-dashboard',
        type: `${viewMode}-${this.reportName}`,
        implementation: 'pm',
      })
      .subscribe({
        next: resp => {
          if (resp && resp['headers']) {
            this.gridData.headers = resp['headers'];
            this.originalHeaders = [...resp['headers']];
          }
          this.getDrilldownData();
        },
        error: err => {
          console.warn('There was a network error getting grid header data.');
          this.loading = false;
        },
      });
  }

  dataStateChange(ev) {
    this.loading = true;
    this.take = ev.take;
    this.skip = ev.skip;
    this.getDrilldownData(this.currentGroup, this.activeSelections);
  }

  // Builds the dynamic report payload, layering drilldown row selections on top of the filter-bar values.
  getDrilldownData(
    level: string = 'carrier',
    drilldownSelections?: Record<string, unknown>
  ) {
    this.gridData.data = [];
    this.gridData.total = 0;
    this.loading = true;
    const viewMode = this.getViewMode(this.view);
    const reportParams = {
      take: this.take,
      skip: this.skip,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      group: this.groupByLevel[level] ?? level,
      name: `PM ${viewMode} Contract Summary Carrier Drilldown`,
    };

    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
      'userId',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    // Add the clicked row's values (e.g. carrier, productType) as scoped selections.
    if (drilldownSelections) {
      Object.entries(drilldownSelections).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          reportParams['search'][key] = {
            selected: Array.isArray(value) ? value : [value],
          };
        }
      });
    }

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReport(reportParams).subscribe({
      next: resp => {
        if (resp && resp['data']) {
          this.gridData.data = resp['data'];
          this.gridData.total = resp['total'] ?? resp['data'].length;
        }
        this.loading = false;
        this.cdRef.markForCheck();
      },
      error: err => {
        console.warn(err);
        this.loading = false;
      },
    });
  }

  // True when this column can be grouped into: a hierarchy level other than the
  // current group and not the terminal Policies level (which navigates instead).
  canDrilldown(dataField: string): boolean {
    if (dataField === this.currentGroup || dataField === 'policy') {
      return false;
    }
    return this.drilldownHierarchy.indexOf(dataField) >= 0;
  }

  // Routes a grid click to the next drilldown level, scoping the request by the clicked row's current-level identity.
  drilldown(dataField, dataItem) {
    // Terminal level: navigate to book management instead of regrouping.
    if (dataField === 'policy') {
      this.handleItemClick(dataItem, 'book-management');
      return;
    }

    // Clicking the current group's own (name) column is a no-op.
    if (dataField === this.currentGroup) {
      return;
    }

    // Only known hierarchy columns drill; ignore anything else.
    if (this.drilldownHierarchy.indexOf(dataField) < 0) {
      return;
    }

    // Add the clicked row's current-group identity (the one string cell) to the running scope.
    const scopeKey = this.scopeKeyByLevel[this.currentGroup];
    if (scopeKey) {
      this.activeSelections = {
        ...this.activeSelections,
        [scopeKey]: [dataItem[this.currentGroup]],
      };
    }

    this.currentGroup = dataField;
    this.skip = 0;
    this.updateDrilldownColumns(dataField);
    this.setBreadcrumb(dataField);
    this.getDrilldownData(dataField, this.activeSelections);
  }

  // Resets the grid and breadcrumb back to the top-level carrier grouping.
  resetDrilldown(getData = false) {
    this.currentGroup = 'carrier';
    this.activeSelections = {};
    this.skip = 0;
    this.updateDrilldownColumns('carrier');
    this.setBreadcrumb('carrier');
    if (getData) this.getDrilldownData('carrier');
  }

  // Navigates back up to the grouping level captured by the clicked ancestor pill.
  goToCrumb(level: string) {
    const orderedKeys = Object.keys(this.activeSelections);
    const levelIndex = orderedKeys.indexOf(level);
    if (levelIndex < 0) {
      return;
    }

    // Drop the clicked level's selection and everything below it, keeping ancestors.
    const retained: Record<string, string[]> = {};
    orderedKeys.slice(0, levelIndex).forEach(key => {
      retained[key] = this.activeSelections[key];
    });
    this.activeSelections = retained;

    this.currentGroup = level;
    this.skip = 0;
    this.updateDrilldownColumns(level);
    this.setBreadcrumb(level);
    this.getDrilldownData(level, this.activeSelections);
  }

  // Rebuilds the breadcrumb: current grouping label plus a pill per accumulated scope selection.
  private setBreadcrumb(group: string) {
    this.currentLevelLabel = this.groupLabels[group] ?? group;
    this.drilldownCrumbs = Object.entries(this.activeSelections)
      .filter(([, value]) => value != null && value.length > 0)
      .map(([key, value]) => ({
        level: key,
        levelLabel: this.groupLabels[key] ?? key,
        label: `${this.fieldLabels[key] ?? key}: ${value.join(', ')}`,
      }));
  }

  // Rebuilds the visible columns from the original set, dropping any hierarchy level above the clicked one.
  private updateDrilldownColumns(level: string) {
    const levelIndex = this.drilldownHierarchy.indexOf(level);
    if (levelIndex < 0) {
      return;
    }

    this.gridData.headers = this.originalHeaders.filter(header => {
      const headerIndex = this.drilldownHierarchy.indexOf(header.DataField);
      // Keep non-hierarchy columns and any level at or below the clicked one.
      return headerIndex === -1 || headerIndex >= levelIndex;
    });
  }

  // Producer-level actions: rep-view button uses 'book-management/rep-reports', grid button uses 'book-management'.
  handleItemClick(dataItem, destination) {
    const gridName =
      this.view === 'carrier'
        ? 'CarrierPolicyDetails'
        : 'BookManagementFilters';
    const filterObj = {
      logic: 'and',
      filters: [],
    };
    const navRoute = destination;

    if (
      navRoute === 'book-management' ||
      navRoute === 'carrier-policy-details'
    ) {
      if (this.filterValues['start'] && this.filterValues['end']) {
        filterObj['filters'].push(
          {
            field: 'IssueDate',
            operator: 'start',
            value: this.filterValues['start'],
          },
          {
            field: 'IssueDate',
            operator: 'end',
            value: this.filterValues['end'],
          }
        );
      }

      if (this.filterValues['unitId']) {
        filterObj['filters'].push({
          field: 'unitID',
          operator: 'selected',
          value: [this.filterValues['unitId']],
        });
      }

      const carrierDrilldown = this.drilldownCrumbs.find(
        crumb => crumb.level === 'carrier'
      );

      if (
        this.filterValues['carrier'] ||
        (dataItem.carrier && isNaN(dataItem.carrier * 1)) ||
        carrierDrilldown
      ) {
        filterObj['filters'].push({
          field: 'Carrier',
          operator: 'selected',
          value: this.filterValues['carrier'] ||
            this.activeSelections['carrier'] || [dataItem.carrier],
        });
      }

      if (this.filterValues['accountType']) {
        filterObj['filters'].push({
          field: 'IRSCode',
          operator: 'selected',
          value: this.filterValues['accountType'],
        });
      }

      const productTypeDrilldown = this.drilldownCrumbs.find(
        crumb => crumb.level === 'productType'
      );

      if (
        this.filterValues['productType'] ||
        (dataItem.productType && isNaN(dataItem.productType * 1)) ||
        productTypeDrilldown
      ) {
        filterObj['filters'].push({
          field: 'ProductType',
          operator: 'selected',
          value: this.filterValues['productType'] ||
            this.activeSelections['productType'] || [dataItem.productType],
        });
      }

      const productNameDrilldown = this.drilldownCrumbs.find(
        crumb => crumb.level === 'productName'
      );

      if (
        this.filterValues['productName'] ||
        (dataItem.productName && isNaN(dataItem.productName * 1)) ||
        productNameDrilldown
      ) {
        filterObj['filters'].push({
          field: 'Name',
          operator: 'selected',
          value: this.filterValues['productName'] ||
            this.activeSelections['productName'] || [dataItem.productName],
        });
      }

      if (dataItem.NPN) {
        filterObj['filters'].push({
          field: 'NIPR',
          operator: 'selected',
          value: dataItem.NPN,
        });
      } else {
        if (this.filterValues['npn']?.filter(npn => npn).length) {
          filterObj['filters'].push({
            field: 'NIPR',
            operator: 'selected',
            value: this.filterValues['npn'],
          });
        }
      }

      if (
        this.filterValues['userId'] &&
        this.filterValues['userId'].filter(userIdItem => userIdItem).length &&
        (this.headerType === 'unit' || this.headerType === 'carrier')
      ) {
        filterObj['filters'].push({
          field: 'userId',
          operator: 'selected',
          value: this.filterValues['userId'],
        });
      } else if (
        this.headerType === 'rep' &&
        !filterObj['filters'].find(filterItem => filterItem.field === 'NIPR')
      ) {
        filterObj['filters'].push({
          field: 'userId',
          operator: 'selected',
          value: [this.ss.get('globals').user.id],
        });
      }

      this.ss.set(gridName, filterObj);
      this.route.navigate([`/${navRoute}`]);
    } else if (navRoute === 'book-management/rep-reports') {
      const repFilters = {
        npn: [dataItem.NPN],
      };
      this.ss.set('BookManagementWidget', repFilters);
      this.route.navigate([`/${navRoute}`]);
    }
  }

  // Formats numeric cell values with locale grouping, leaving non-numeric values (e.g. names, IDs) untouched.
  formatNumber(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const numeric = Number(value);
    if (typeof value === 'boolean' || Number.isNaN(numeric)) {
      return String(value);
    }
    return this.decimalPipe.transform(numeric) ?? String(value);
  }

  emitFilters(ev) {
    const reportParams = { search: {} };
    Object.entries(this.activeSelections).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        reportParams['search'][key] = {
          selected: Array.isArray(value) ? value : [value],
        };
      }
    });

    const nonFilteredKeys = Object.values(this.scopeKeyByLevel).filter(
      scope => !Object.keys(reportParams.search).includes(scope)
    );
    if (nonFilteredKeys.length > 0) {
      nonFilteredKeys.forEach(keyItem => {
        reportParams['search'][keyItem] = {
          selected: null,
        };
      });
    }
    this.emittingFilters = true;
    this.emitDrilldownFilter.emit(reportParams);
  }

  canEmitFilters() {
    return Object.values(this.activeSelections).length > 0;
  }
}
